import { Plugin, EditorState } from 'prosemirror-state';
import { Node as ProsemirrorNode, Schema } from 'prosemirror-model';
import { toggleMark } from 'prosemirror-commands';
import { MenuItem, createMarkItem, createImageItem } from './MenuItem';
import { coordsAtPos } from '../utils/coords';
import { normalizeUri } from '../utils/uri';
import { selectMarkAtCursor, getMarkAtCursor, currentMark, rangeIsMark } from '../utils/selection';
import { EditorView } from 'prosemirror-view';
import clipboardCopy from 'clipboard-copy';
import { uiStatePluginKey } from './keys';

class FloatingMenuView {
  items;
  editorView;
  name;
  open;
  updatedAt;
  coords;
  node;
  constructor(schema, editorView) {
    this.editorView = editorView;
    this.name = 'floating-menu-plugin';
    this.open = '';
    this.updatedAt = Date.now();
    this.coords = { start: {}, end: {} };
    this.node = null;

    this.items = [];
    this.initItems(schema);
  }

  update(view, lastState) {
    const { state } = view;

    // Clear open state if the document/selection didn't change
    if (
      (lastState && lastState.doc.eq(state.doc) && lastState.selection.eq(state.selection)) ||
      uiStatePluginKey.getState(lastState).readOnly
    ) {
      this.open = '';
      this.updatedAt = Date.now();
      return;
    }

    // Otherwise, reposition it and update its content
    const { from, to } = state.selection;

    // These are in screen coordinates
    // We can't use EditorView.cordsAtPos here because it can't handle linebreaks correctly
    // See: https://github.com/ProseMirror/prosemirror-view/pull/47
    const start = coordsAtPos(view, from);
    const end = coordsAtPos(view, to, true);

    const activeItem = this.items.find(
      item =>
        lastState &&
        lastState.doc.eq(state.doc) &&
        item.statusCheck &&
        item.statusCheck(this.editorView.state) === 'active'
    ) || { name: '' };
    if (this.open !== activeItem.name) {
      this.open = activeItem.name;
    }
    this.coords = { start, end };
    this.updatedAt = Date.now();
  }

  destroy() {
    /* noop */
  }

  initItems(schema) {
    const link = createMarkItem({
      spec: schema.marks.link,
      name: 'link',
      statusCheck: (editorState) => {
        const { from, to } = editorState.selection;
        return rangeIsMark(editorState.doc, from, to, schema.marks.link) ? 'active' : 'enabled';
      },
      command: () =>
        this.editorView.dispatch(this.editorView.state.tr.setMeta('prompt-menu-plugin', 'link'))
    });

    link.editCommands = [
      {
        name: 'visit',
        command: () => {
          const link = currentMark(
            this.editorView.state.selection.$from.doc,
            this.editorView.state.selection.from,
            schema.marks.link
          );

          if (!link) return;

          const { href } = link.attrs;
          window.open(normalizeUri(href));
          this.close();
        }
      },
      {
        name: 'copy',
        command: () => {
          const link = currentMark(
            this.editorView.state.selection.$from.doc,
            this.editorView.state.selection.from,
            schema.marks.link
          );

          if (!link) return;

          const { href } = link.attrs;
          clipboardCopy(href).catch(e => {
            this.editorView.dispatch(this.editorView.state.tr.setMeta('clipboardCopyError', e));
          });
          this.close();
        }
      },
      {
        name: 'edit',
        command: () => {
          const mark = getMarkAtCursor(this.editorView, schema.marks.link);
          if (mark) {
            this.close();
            this.editorView.dispatch(
              this.editorView.state.tr.setMeta('prompt-menu-plugin', {
                type: 'link',
                attrs: mark.attrs
              })
            );
          }
        }
      },
      {
        name: 'remove',
        command: () => {
          selectMarkAtCursor(this.editorView, schema.marks.link);
          toggleMark(schema.marks.link)(this.editorView.state, this.editorView.dispatch);
          this.close();
        }
      }
    ];

    const image = createImageItem({
      spec: schema.nodes.image_with_link,
      name: 'image',
      statusCheck(state) {
        let { node } = state.selection;
        if (node) {
          return node.type.name === 'image_with_link' ? 'active' : 'disabled';
        } else {
          return 'enabled';
        }
      },
      command: () =>
        this.editorView.dispatch(this.editorView.state.tr.setMeta('prompt-menu-plugin', 'image'))
    });

    image.editCommands = [
      {
        name: 'visit',
        isEnabled: () => {
          const { node } = this.editorView.state.selection;
          return node && node.attrs.href;
        },
        command: () => {
          const { node } = this.editorView.state.selection;

          if (!node || !node.attrs.href) return;

          const { href } = node.attrs;
          window.open(normalizeUri(href));
        }
      },
      {
        name: 'edit',
        command: () => {
          const { node } = this.editorView.state.selection;
          if (node.type.name !== 'image_with_link') return;

          this.close();
          const { src, ...rest } = node.attrs;
          rest.img_src = src;
          this.editorView.dispatch(
            this.editorView.state.tr.setMeta('prompt-menu-plugin', {
              type: 'image',
              attrs: rest
            })
          );
        }
      },
      {
        name: 'delete',
        command: () => {
          const { node } = this.editorView.state.selection;
          if (node.type.name === 'image_with_link') {
            this.editorView.dispatch(this.editorView.state.tr.deleteSelection());
          }
          this.close();
        }
      }
    ];

    this.items = [link, image];
  }

  close() {
    this.open = '';
    this.updatedAt = Date.now();
    this.editorView.dispatch(this.editorView.state.tr.setMeta(this.name, ''));
    this.editorView.focus();
  }
}

export default (schema) => {
  return new Plugin({
    view(editorView) {
      return new FloatingMenuView(schema, editorView);
    }
  });
};



// WEBPACK FOOTER //
// ./app/javascript/client/editor-core/plugins/FloatingMenuPlugin.js